import React, { ForwardedRef, useState, useEffect, useRef } from 'react';
import { feedback } from '@ecosystem/newron-design-system';
import { SegmentedChartData } from './data';
import { DataColumn, FormatHint } from '../../types';
import { Legend } from '../../design/charts/legend';
import { SlopeChart, SlopeChartOptions, StackedBarChart } from '../../design/charts';
import { formatScaleValue, formatTooltipValue } from '../data/common';
import { LabelPosition } from '../../design/charts/types';
import classNames from 'classnames';
import { useMobile } from '../../context/mobile/mobile.provider';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { CHART_TITLE_HEIGHT } from '../../design/charts/constants';
import { useScrollbarHeight } from '../../design/charts/useScrollbarHeight.hook';
import { getChartHeight } from '../../design/charts/utils';

type SegmentEntry = {
  Chart: JSX.Element;
  title: string;
};

type Props = {
  chartData: SegmentedChartData;
  yColumn: DataColumn;
  currency: string;
  trackTooltipEvent: () => void;
  chartFullscreen?: boolean;
};

const { Tooltip } = feedback;

const SegmentationChart = React.forwardRef<HTMLDivElement, Props>(
  ({ chartData, yColumn, currency, trackTooltipEvent, chartFullscreen }: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { segmentDataSets, maxYRange, dataLabels } = chartData;
    const { isMobile } = useMobile();
    const [isLandscape, setIsLandscape] = useState(
      typeof window !== 'undefined' && window.innerWidth > window.innerHeight
    );
    const chartOverflowContainerRef = useRef<HTMLDivElement>(null);
    const scrollbarHeight = useScrollbarHeight(chartOverflowContainerRef);
    const isChartFullView = isLandscape || chartFullscreen;
    const chartHeightOffset = isMobile ? 2 * CHART_TITLE_HEIGHT + scrollbarHeight : 0;

    useEffect(() => {
      const handleOrientationChange = () => {
        setIsLandscape(window.innerWidth > window.innerHeight);
      };

      window.addEventListener('resize', handleOrientationChange);
      window.addEventListener('orientationchange', handleOrientationChange);

      return () => {
        window.removeEventListener('resize', handleOrientationChange);
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }, []);

    let info;
    let segmentEntries: SegmentEntry[] = [];
    if (yColumn.canAggregate) {
      segmentEntries = segmentDataSets.map((set, index) => {
        const options = {
          yScale: {
            stacked: true,
            formatter: value => formatScaleValue(value, { yColumn }),
            useMax: index !== 0 ? maxYRange : undefined,
          },
          tooltip: {
            valueFormatter: value => formatTooltipValue(value, { yColumn, currency }),
          },
          data: {
            labels: dataLabels,
            sets: set.dataSets,
            summary: set.totals,
            additionalSummary: set.additionalTotals,
          },
        };
        const entry: SegmentEntry = {
          title: set.label,
          Chart: (
            <StackedBarChart
              options={options}
              trackTooltipEvent={trackTooltipEvent}
              isMobile={isMobile}
              isChartFullView={isChartFullView}
              chartHeightOffset={chartHeightOffset}
            />
          ),
        };
        return entry;
      });
    } else {
      segmentEntries = segmentDataSets.map((set, index) => {
        const options: SlopeChartOptions = {
          yScale: {
            formatter: value => formatScaleValue(value, { yColumn }),
            useMax: index !== 0 ? maxYRange : undefined,
          },
          tooltip: {
            valueFormatter: value => formatTooltipValue(value, { yColumn, currency }),
          },
          data: {
            labels: dataLabels,
            sets: set.dataSets,
            summary: set.totals,
            additionalSummary: set.additionalTotals,
          },
        };

        const entry: SegmentEntry = {
          title: set.label,
          Chart: (
            <SlopeChart
              options={options}
              trackTooltipEvent={trackTooltipEvent}
              isMobile={isMobile}
              isChartFullView={isChartFullView}
              chartHeightOffset={chartHeightOffset}
            />
          ),
        };
        return entry;
      });
    }

    info = {
      summaryEntry: segmentEntries[0],
      segmentEntries: segmentEntries.slice(1),
      chartData,
    };

    if (!info) {
      return <EmptyState height="500px" />;
    }

    return (
      <div
        ref={ref}
        className={classNames('w-full flex flex-rows gap-2', {
          'flex-column-reverse': isMobile,
        })}
      >
        <Legend
          css="pt-10"
          data={info.chartData.segmentDataSets[0].dataSets}
          totalsLabel={info.chartData.totalsLabel}
          totalsChangeLabel={info.chartData.totalsChangeLabel}
          additionalTotalsLabel={info.chartData.additionalTotalsLabel}
          additionalTotalsChangeLabel={info.chartData.additionalTotalsChangeLabel}
          additionalLabelPosition={
            [FormatHint.Percentage, FormatHint.Index].includes(yColumn.formatHint)
              ? LabelPosition.TOP
              : LabelPosition.BOTTOM
          }
        />
        {isMobile ? (
          <div
            ref={chartOverflowContainerRef}
            className="flex justify-around gap-2 overflow-auto"
            style={{ height: getChartHeight(isMobile, isChartFullView, true, chartHeightOffset - CHART_TITLE_HEIGHT) }}
          >
            <div className="flex">
              <SegmentChart entry={info.summaryEntry} isSummary isMobile />
            </div>
            <div className="relative w-full flex">
              {info.segmentEntries.map((entry, idx) => (
                <SegmentChart key={`segment_${idx * 2}`} entry={entry} isMobile />
              ))}
            </div>
          </div>
        ) : (
          <>
            <SegmentChart entry={info.summaryEntry} isSummary />
            <div className="relative flex-grow flex items-stretch overflow-x-auto">
              {info.segmentEntries.map((entry, idx) => (
                <SegmentChart key={`segment_${idx}`} entry={entry} />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
);

type SegmentChartProps = {
  entry: SegmentEntry;
  isSummary?: boolean;
  isMobile?: boolean;
};
function SegmentChart({ entry, isSummary, isMobile }: Readonly<SegmentChartProps>) {
  const { title, Chart } = entry;
  const isLongLabel = title.length > 30;
  return (
    <div
      className={classNames('chart-container relative flex flex-column h-full items-center', {
        'border-r border-light pr-4': isSummary,
        'pb-6': !isMobile,
      })}
    >
      <Tooltip placement="top">
        <div className={`${isSummary && 'px-8'} ${isLongLabel && 'truncate'} text-center text-sm h-10 max-w-44 mx-1`}>
          {title}
        </div>
        {isLongLabel && <Tooltip.Content>{title}</Tooltip.Content>}
      </Tooltip>
      <div className="flex-grow">{Chart}</div>
    </div>
  );
}

export { SegmentationChart };
